// ============================================================================
// src/app/data/surfaceDataManifest.ts — B3.3 Prerequisite A
// ============================================================================
// Plain data. No React, no component imports, no Supabase client.
//
// IT IS NO LONGER LOADABLE BY RAW NODE. Migrated-surface discovery is an eager
// `import.meta.glob`, which only a Vite-family transform resolves. The browser
// entry calls assertSurfaceDataManifestRuntime() before React renders, so this
// module and both eager declaration globs are now in the production graph as
// well as the Vitest graph. That call is synchronous validation only: it does
// not inspect a route, authentication state, query client, or route
// disposition.
//
// WHAT A SURFACE IS
//
// Not a route row. A surface is whatever the existing dispatch actually SELECTS
// to render, which is a strictly larger set than the route table:
//
//   * `/` renders LightHomePortal or HomeDashboard depending on role, and the
//     Light portal consumes a genuinely narrower set of data. One row, two
//     surfaces.
//   * `/cattle/herds/*` reaches a router that mounts the animal record for a
//     non-empty suffix and the hub only at the index. One row, two surfaces —
//     and wrapping the wildcard with the hub loader would overfetch on every
//     record URL.
//   * The full-screen broiler batch form is keyed on `showForm`, not on `view`,
//     so no route row can express it at all.
//
// Loading data per ROUTE ROW would therefore be wrong in three different ways
// before the migration even starts. Surfaces are the unit that matches what
// renders.
//
// WHAT THIS FILE DECLARES TODAY
//
// Complete ownership inventory. Every authenticated surface declares its
// route-local obligations. `legacy.all` remains a validation sentinel for a
// missing declaration; it does not authorize or invoke an all-data loader.
// The manifest establishes ownership vocabulary, while the separate ledger,
// path fence, boundary, and renderer establish a released mount.
//
// The invariant that makes the migration checkable is one owner per obligation:
// every render obligation of every surface is owned by exactly one of
//
//   legacy.<source>   — retained compatibility/sentinel vocabulary (temporary)
//   permanent         — a catalog resource with a stable key and pure query
//   temporary-local   — an existing page-local read, inventoried until it moves
//   no-server-read    — a receipt-backed surface with no route-local server
//                       read to own (separately-owned shell/external chrome is
//                       named explicitly in its receipt)
//
// Missing obligations, duplicate owners, and undeclared physical calls all fail.
// That is what stops a read quietly gaining a second owner while the first is
// left in place — the failure that would make every later "we localized this"
// claim false.
// ============================================================================

// ---------------------------------------------------------------------------
// The vocabulary, written as types so "one owner per obligation" is checkable
// rather than described.
// ---------------------------------------------------------------------------

/** Who owns a render obligation. Exactly one of four, never zero, never two. */
export type ObligationOwner = 'legacy' | 'permanent' | 'temporary-local' | 'no-server-read';

/**
 * One render obligation. `id` is a `legacy.<source>` compatibility name for an
 * unresolved declaration, and a catalog resource id once it has a local owner.
 */
export type Obligation = {
  readonly id: string;
  readonly owner: ObligationOwner;
};

/**
 * A remaining dependency on legacy App boot or an App-owned context.
 *
 * These are deliberately separate from `obligations`: a permanent route
 * resource still has exactly one owner even while a page consumes another
 * value that App's legacy transaction publishes. Mixing the two concepts would
 * either make the resource look double-owned or hide the remaining boot work.
 */
export const RESIDUAL_LEGACY_IDS = Object.freeze([
  'legacy.boot.dataLoaded',
  'legacy.context.batches.batches',
  'legacy.context.pig.planner',
  'legacy.context.pig.historyInputs',
  // RETIRED, and kept in the vocabulary the way the five other unclaimed ids
  // above and below are: the enum records what a residual WAS, so a later
  // reader can tell "no surface declares this" from "this concept never
  // existed". No surface holds either id.
  //
  // /pig/breeding's save was `persistBreeding` — a PigEntry prop whose body was
  // `sbSave('ppp-breeding-v1', nb)` — beside PigContext's raw
  // `setBreedingCycles`. Both halves went in the same change. The write moved
  // to src/lib/appStoreBlobApi.ts, which is App's `sbSave` body itself (App now
  // delegates to it), so the retry / 8s-race / never-throws / setSaveStatus
  // posture is identity rather than a route-local copy. The mirror was dropped
  // because it had NO reader: PigFeedView stopped calling usePig() entirely,
  // PigBatchesView and PigBatchPage call it for form state only, and App's own
  // read was the `resolveSire` closure both of its consumers had stopped
  // calling.
  'legacy.context.pig.breeding',
  // /pig/farrowing's twin, retired the same way and in the same change —
  // `persistFarrowing` moved to the shared blob writer and `setFarrowingRecs`
  // was deleted. That mirror was the starker case of the two: not one module in
  // src/, App included, destructures `farrowingRecs` off usePig(). It was a
  // write nothing read. Its feeder-batch companion retired once scheduled
  // maintenance became the source-derived webform mirror owner (below).
  'legacy.context.pig.farrowing',
  // RETIRED after the scheduled maintenance owner took webform_config mirror
  // publication. Farm-born batches and pig batch edits now write
  // ppp-feeders-v1 through the shared app_store writer with route-local
  // optimistic state; no released route calls App's feeder context or its
  // legacy-only maintenance trigger.
  'legacy.context.pig.feeders',
  // RETIRED, and kept for the same reason as the two above. /pig/sows held
  // THREE App writes as ONE id — `persistBreeders`, `persistBreedOptions` and
  // `persistOriginOptions` — precisely because no release could take one
  // without the others, and all three moved together to
  // src/lib/appStoreBlobApi.ts. The two option KEYS did not move and are still
  // created by that page the first time an operator adds a custom breed or
  // origin; only the writer changed.
  //
  // ONE MIRROR SURVIVED THE RETIREMENT ON PURPOSE. `setBreeders` is still
  // called there, because App's `triggerMaintenance` snapshot default reads
  // PigContext's `breeders` and that value decides `active_groups` membership
  // through computeSubCurrentCount. It does not hold the surface partial: it is
  // a context setter reached through usePig(), so the page WRITES INTO an
  // App-owned context rather than DEPENDING ON one, which is the same rule
  // view.webforms states as "a setter is not a read". The two option mirrors
  // had no reader anywhere and were dropped.
  'legacy.context.pig.breeders',
  // RETIRED. /layer/groups now writes ppp-layer-groups-v1 through the shared
  // appStoreBlobApi transport seam and publishes only setLayerGroups(next) as a
  // non-consuming mirror for App's remaining feed-cost fan-out. Its render data
  // comes from layer.groups.core; the session-free maintenance-cron service owns
  // the derived webform_config publication that the former App callback also
  // requested. The id stays in the vocabulary so a future route cannot restore
  // the App callback without declaring the coupling it reintroduces.
  'legacy.context.layer.groups',
  'legacy.context.layer.batches',
  'legacy.context.layer.housings',
  // NO HOLDER. Both were the /dailys hub's, and both are retired: the hub reads
  // the ppp-webforms-v1 document itself through webform.pigDailys.config, and
  // the wfGroups mirror was DELETED rather than replaced — its only writer was
  // the hub's own bridge and its only reader the hub's fallback for the value
  // that bridge had just produced. They are kept in the vocabulary rather than
  // removed so a future reader of a receipt or a lane document can still resolve
  // the names, and so re-introducing either coupling has an id to declare
  // instead of an excuse to skip one.
  'legacy.context.webformsConfig.wfGroups',
  'legacy.context.webformsConfig.webformsConfig',
  'legacy.context.dailysRecent.broiler',
  'legacy.context.dailysRecent.pig',
  'legacy.context.dailysRecent.layer',
  'legacy.context.dailysRecent.cattle',
  'legacy.context.dailysRecent.sheep',
  'legacy.context.feedCosts.missedCleared',
] as const);

export type ResidualLegacyId = (typeof RESIDUAL_LEGACY_IDS)[number];

/**
 * A surface can own permanent route resources without being eligible for a
 * released render claim. `partial` means exactly that: the permanent resource
 * receipt is valid, but one or more `residualLegacy` dependencies still remain.
 */
export const SURFACE_MIGRATION_STATUS = Object.freeze({
  LEGACY: 'legacy',
  PARTIAL: 'partial',
  PERMANENT: 'permanent',
  NO_SERVER_READ: 'no-server-read',
} as const);

export type SurfaceMigrationStatus = (typeof SURFACE_MIGRATION_STATUS)[keyof typeof SURFACE_MIGRATION_STATUS];

/** A surface split declared by VIEW_VARIANTS: what renders, and when. */
export type SurfaceVariant = {
  readonly surface: string;
  readonly matchContract: string;
};

/**
 * How a pathname-reading component relates to surfaces. `splits` is the only
 * disposition that owns one; the other three record WHY a component that reads
 * the URL is not a surface boundary, so a new router cannot appear undecided.
 */
export type RouterDisposition = 'splits' | 'record-state' | 'sub-mode' | 'not-a-surface';

export type SurfaceRouter = {
  readonly disposition: RouterDisposition;
  /** the view whose single route row this component splits; absent when it splits none */
  readonly view?: string;
  /** the components a `splits` router mounts, listed so the two tables cannot drift */
  readonly mounts?: readonly string[];
  readonly reason: string;
};

/** A surface the route tables cannot enumerate because it is not keyed on `view`. */
export type RouteOracleException = {
  readonly surface: string;
  readonly authenticated: boolean;
  readonly matchContract: string;
};

/** One manifest entry. `view` is null for a surface no route row names. */
export type SurfaceEntry = {
  readonly surface: string;
  readonly view: string | null;
  readonly matchContract: string;
  readonly migrationStatus: SurfaceMigrationStatus;
  readonly obligations: readonly Obligation[];
  readonly residualLegacy: readonly ResidualLegacyId[];
};

// The authenticated view space. Frozen and explicit rather than derived at
// runtime: a new route must be declared here deliberately, and the guard
// cross-checks this list against live VIEW_TO_PATH so it cannot go stale
// silently. `newsletter` is excluded — it is the one public surface and lives
// in ROUTE_ORACLE_EXCEPTIONS. `weighinsessions` is included even though it has
// no VIEW_TO_PATH entry: it is URL-reachable only, at /weigh-in-sessions/<id>.
// `processingCutSheet` is the second such view, URL-reachable only at
// /processing/cut-sheet/<recordId>: every address under it names one
// Processing record, so there is no bare path for VIEW_TO_PATH to hold.
// `equipmentAdminRecord` is the third, URL-reachable only at
// /admin/equipment/<slug> (A40), for the same reason.
export const AUTHENTICATED_VIEWS: readonly string[] = Object.freeze([
  'activity',
  'addfeed',
  'animalHistory',
  'breeding',
  'broilerHome',
  'broilerdailys',
  'broilerweighins',
  'cattleHome',
  'cattlebatches',
  'cattlebreeding',
  'cattledailys',
  'cattleforecast',
  'cattleherds',
  'cattlelog',
  'cattleweighins',
  'clientErrors',
  'eggdailys',
  'equipmentAdminRecord',
  'equipmentHome',
  'farrowing',
  'feed',
  'fuelSupply',
  'fuelingHub',
  'home',
  'layerbatches',
  'layerdailys',
  'layers',
  'layersHome',
  'list',
  'mySubmissions',
  'newsletterAdmin',
  'pastureMap',
  'pigbatches',
  'pigdailys',
  'pigs',
  'pigsHome',
  'pigweighins',
  'processing',
  'processingCutSheet',
  'production',
  'projects',
  'sheepHome',
  'sheepbatches',
  'sheepdailys',
  'sheepflocks',
  'sheepweighins',
  'sows',
  'tasks',
  'tasksWebform',
  'timeline',
  'weather',
  'webform',
  'webformhub',
  'webforms',
  'weighins',
  'weighinsessions',
]);

// Views whose single route row renders more than one surface. The parent view
// gets NO surface of its own — declaring both a parent and its variants would
// create two owners for the same render, which is the exact ambiguity the
// one-owner rule exists to prevent.
//
// This list used to hold two entries, `home` and `cattleherds`, and the guard
// that checked it compared the manifest to VIEW_TO_PATH — the same route table
// the manifest exists because it is too coarse. So the manifest was validating
// itself, and thirteen further splits were simply absent. They are derived
// mechanically now: tests/static/helpers/surfaceRouters.js reads live source
// for every component that makes a render decision from the URL, and the guard
// requires this table to account for each one.
//
// The rule for what earns a surface is mechanical, so it is not re-litigated:
// a pathname-selected branch that mounts a DIFFERENT COMPONENT is a different surface, because
// the plan's own rationale — wrapping a wildcard with the hub loader overfetches
// on every record URL — is an argument about which component mounts. A branch
// that re-renders the SAME component in another mode is one surface, and its
// modes are written into the match contract instead.
export const VIEW_VARIANTS: Readonly<Record<string, readonly SurfaceVariant[]>> = Object.freeze({
  home: Object.freeze([
    {surface: 'app.home.standard', matchContract: 'AppEntry "/" element, non-Light branch'},
    {surface: 'app.home.light', matchContract: 'AppEntry "/" element, isLight branch'},
  ]),
  cattleherds: Object.freeze([
    {surface: 'cattle.herds.index', matchContract: 'CattleHerdsRouter, empty suffix'},
    {surface: 'cattle.herds.detail', matchContract: 'CattleHerdsRouter, non-empty suffix → CattleAnimalPage'},
  ]),
  cattledailys: Object.freeze([
    {surface: 'cattle.dailys.index', matchContract: 'CattleDailysRouter, no /cattle/dailys/ suffix → CattleDailysHub'},
    {surface: 'cattle.dailys.detail', matchContract: 'CattleDailysRouter, /cattle/dailys/<id> → CattleDailyPage'},
  ]),
  cattlebatches: Object.freeze([
    {surface: 'cattle.batches.index', matchContract: 'CattleBatchesRouter, no suffix → CattleBatchesHub'},
    {surface: 'cattle.batches.detail', matchContract: 'CattleBatchesRouter, /cattle/batches/<id> → CattleBatchPage'},
    {
      surface: 'cattle.batches.forecastDetail',
      matchContract:
        'CattleBatchesRouter, /cattle/batches/forecast/<monthKey> → CattleForecastBatchPage. A live projection keyed by month, NOT a cattle_processing_batches row, so it can never share the record resource.',
    },
  ]),
  list: Object.freeze([
    {
      surface: 'broiler.batches.index',
      matchContract: 'BroilerListRouter, no /broiler/batches/ prefix → BroilerListHub',
    },
    {surface: 'broiler.batches.detail', matchContract: 'BroilerListRouter, /broiler/batches/<id> → BroilerBatchPage'},
  ]),
  broilerdailys: Object.freeze([
    {surface: 'broiler.dailys.index', matchContract: 'BroilerDailysRouter, no suffix → BroilerDailysHub'},
    {surface: 'broiler.dailys.detail', matchContract: 'BroilerDailysRouter, /broiler/dailys/<id> → PoultryDailyPage'},
  ]),
  layerbatches: Object.freeze([
    {surface: 'layer.batches.index', matchContract: 'LayerBatchesRouter, no suffix → LayerBatchesHub'},
    {surface: 'layer.batches.detail', matchContract: 'LayerBatchesRouter, /layer/batches/<id> → LayerBatchPage'},
    {surface: 'layer.housings.detail', matchContract: 'LayerBatchesRouter, /layer/housings/<id> → LayerHousingPage'},
  ]),
  layerdailys: Object.freeze([
    {surface: 'layer.dailys.index', matchContract: 'LayerDailysRouter, no suffix → LayerDailysHub'},
    {surface: 'layer.dailys.detail', matchContract: 'LayerDailysRouter, /layer/dailys/<id> → LayerDailyPage'},
  ]),
  eggdailys: Object.freeze([
    {surface: 'layer.eggDailys.index', matchContract: 'EggDailysRouter, no suffix → EggDailysHub'},
    {surface: 'layer.eggDailys.detail', matchContract: 'EggDailysRouter, /layer/eggs/<id> → EggDailyPage'},
  ]),
  pigdailys: Object.freeze([
    {surface: 'pig.dailys.index', matchContract: 'PigDailysRouter, no suffix → PigDailysHub'},
    {surface: 'pig.dailys.detail', matchContract: 'PigDailysRouter, /pig/dailys/<id> → PigDailyPage'},
  ]),
  pigbatches: Object.freeze([
    {surface: 'pig.batches.index', matchContract: 'PigBatchesView, recordMode false'},
    {
      surface: 'pig.batches.detail',
      matchContract: 'PigBatchesView, recordMode true (/pig/batches/<id>) → PigBatchPage rendered inline',
    },
  ]),
  sheepdailys: Object.freeze([
    {surface: 'sheep.dailys.index', matchContract: 'SheepDailysRouter, no suffix → SheepDailysHub'},
    {surface: 'sheep.dailys.detail', matchContract: 'SheepDailysRouter, /sheep/dailys/<id> → SheepDailyPage'},
  ]),
  sheepbatches: Object.freeze([
    {surface: 'sheep.batches.index', matchContract: 'SheepBatchesRouter, no suffix → SheepBatchesHub'},
    {surface: 'sheep.batches.detail', matchContract: 'SheepBatchesRouter, /sheep/batches/<id> → SheepBatchPage'},
  ]),
  sheepflocks: Object.freeze([
    {surface: 'sheep.flocks.index', matchContract: 'SheepFlocksRouter, no suffix → SheepFlocksHub'},
    {surface: 'sheep.flocks.detail', matchContract: 'SheepFlocksRouter, /sheep/flocks/<id> → SheepAnimalPage'},
  ]),
  tasks: Object.freeze([
    {
      surface: 'tasks.center',
      matchContract:
        'TasksRouter, /tasks and /tasks/todo → TaskCenterView. ONE surface: the same component with forceMode and a distinct React key, remounted so mode and URL cannot desync.',
    },
    {surface: 'tasks.todo.detail', matchContract: 'TasksRouter, /tasks/todo/<id> → TodoItemPage'},
    {surface: 'tasks.instance.detail', matchContract: 'TasksRouter, /tasks/<id> → TaskInstancePage'},
  ]),
  fuelingHub: Object.freeze([
    {surface: 'equipment.fueling.hub', matchContract: 'FuelingHub, subRoute "hub" — the category clusters'},
    {surface: 'equipment.fueling.form', matchContract: 'FuelingHub, /equipment/<slug> → EquipmentFuelingWebform'},
    {surface: 'equipment.fueling.supply', matchContract: 'FuelingHub, /equipment/supply → FuelSupplyWebform'},
  ]),
  equipmentHome: Object.freeze([
    {
      surface: 'equipment.fleet.index',
      matchContract:
        'EquipmentHome, subView "fleet" or "fuel-log". ONE surface: the fuel log is another view of the same loadAll() arrays, not another mount.',
    },
    {surface: 'equipment.fleet.detail', matchContract: 'EquipmentHome, /fleet/<slug> → EquipmentDetail'},
    {
      surface: 'equipment.fueling.entry',
      matchContract: 'EquipmentHome, /fleet/fueling/<id> → EquipmentFuelingEntryPage',
    },
    {
      surface: 'equipment.checklist.entry',
      matchContract: 'EquipmentHome, /fleet/checklist/<id> → EquipmentChecklistEntryPage',
    },
  ]),
});

// Components in `src/` that read the pathname. Every one must be here, so a new
// router cannot appear without a decision being recorded. The guard derives the
// live list and requires this table to match it exactly — that is what stops
// the manifest from validating itself against the route table again.
//
//   splits            the component selects between surfaces; VIEW_VARIANTS[view]
//                     declares them, and the components it mounts are listed so
//                     the two cannot drift.
//   record-state      its only branches are the RecordPage* loading / error /
//                     not-found / record states of ONE surface. That is the
//                     plan's fail-closed contract, not a surface boundary.
//   sub-mode          re-renders the same component in another mode; one mount,
//                     one load, one surface.
//   not-a-surface     reads the pathname for navigation, telemetry or
//                     highlighting and selects no render from the pathname.
export const RECORD_STATE_COMPONENTS: readonly string[] = Object.freeze([
  'RecordPageLoading',
  'RecordPageLoadError',
  'RecordPageNotFound',
  'RecordPageFrame',
  'RecordPageBody',
]);

export const SURFACE_ROUTERS: Readonly<Record<string, SurfaceRouter>> = Object.freeze({
  'src/App.tsx::App': Object.freeze({
    disposition: 'splits',
    view: 'home',
    // Every branch App returns, listed so a new one fails this table rather
    // than appearing silently. Only the Light/standard pair is a surface split
    // OWNED here: the four pre-gate shells are owned by the login-gate guard
    // (PRE_GATE_RENDERS) and the six domain entries by the route oracle, which
    // is why they carry no VIEW_VARIANTS entry of their own. The deactivated-
    // account card (migration 218) loads nothing and is owned by
    // tests/static/inactive_account_lockout_static.test.js.
    mounts: Object.freeze([
      'LightHomePortal',
      'InactiveAccountScreen',
      'SetPasswordScreen',
      'NewsletterPublicApp',
      'LoginScreen',
      'BootSplash',
      'PageBoundary',
      'BatchForm',
      'WebformsEntry',
      'BroilerEntry',
      'PigEntry',
      'CattleEntry',
      'SheepEntry',
      'LayerEntry',
    ]),
    reason:
      'The root dispatch. Its pre-gate branches are owned by the login-gate guard and its domain branches by the route oracle; the only surface split it owns here is Light vs standard home.',
  }),
  'src/cattle/CattleHerdsView.tsx::CattleHerdsRouter': Object.freeze({
    disposition: 'splits',
    view: 'cattleherds',
    mounts: Object.freeze(['CattleAnimalPage']),
    reason: 'Hub at the index, animal record for a non-empty suffix.',
  }),
  'src/cattle/CattleDailysView.tsx::CattleDailysRouter': Object.freeze({
    disposition: 'splits',
    view: 'cattledailys',
    mounts: Object.freeze(['CattleDailyPage']),
    reason: 'Hub at the index, daily record for /cattle/dailys/<id>.',
  }),
  'src/cattle/CattleBatchesView.tsx::CattleBatchesRouter': Object.freeze({
    disposition: 'splits',
    view: 'cattlebatches',
    mounts: Object.freeze(['CattleBatchPage', 'CattleForecastBatchPage']),
    reason:
      'Three branches: hub, persisted batch record, and a forecast-only projection on its own /forecast/ segment.',
  }),
  'src/broiler/BroilerListView.tsx::BroilerListRouter': Object.freeze({
    disposition: 'splits',
    view: 'list',
    mounts: Object.freeze(['BroilerBatchPage']),
    reason: 'Hub at the index, batch record for /broiler/batches/<id>.',
  }),
  'src/broiler/BroilerDailysView.tsx::BroilerDailysRouter': Object.freeze({
    disposition: 'splits',
    view: 'broilerdailys',
    mounts: Object.freeze(['PoultryDailyPage']),
    reason: 'Hub at the index, daily record for /broiler/dailys/<id>.',
  }),
  'src/layer/LayerBatchesView.tsx::LayerBatchesRouter': Object.freeze({
    disposition: 'splits',
    view: 'layerbatches',
    mounts: Object.freeze(['LayerBatchPage', 'LayerHousingPage']),
    reason: 'Three branches under one view: hub, layer batch record, and housing record on a different URL prefix.',
  }),
  'src/layer/LayerDailysView.tsx::LayerDailysRouter': Object.freeze({
    disposition: 'splits',
    view: 'layerdailys',
    mounts: Object.freeze(['LayerDailyPage']),
    reason: 'Hub at the index, daily record for /layer/dailys/<id>.',
  }),
  'src/layer/EggDailysView.tsx::EggDailysRouter': Object.freeze({
    disposition: 'splits',
    view: 'eggdailys',
    mounts: Object.freeze(['EggDailyPage']),
    reason: 'Hub at the index, egg daily record for /layer/eggs/<id>.',
  }),
  'src/pig/PigDailysView.tsx::PigDailysRouter': Object.freeze({
    disposition: 'splits',
    view: 'pigdailys',
    mounts: Object.freeze(['PigDailyPage']),
    reason: 'Hub at the index, daily record for /pig/dailys/<id>.',
  }),
  'src/pig/PigBatchesView.tsx::PigBatchesView': Object.freeze({
    disposition: 'splits',
    view: 'pigbatches',
    mounts: Object.freeze([]),
    reason:
      'recordMode is computed from /pig/batches/ and gates the record render inline rather than through a return, so the branch set is not visible in this component s returns. Declared explicitly for that reason.',
  }),
  'src/pig/SowsView.tsx::SowsView': Object.freeze({
    disposition: 'sub-mode',
    view: 'sows',
    reason:
      'recordMode selects a breeding-pig record panel inside the SAME component and the same breeders array. One mount, one load — splitting it would create two owners for a single render.',
  }),
  'src/sheep/SheepDailysView.tsx::SheepDailysRouter': Object.freeze({
    disposition: 'splits',
    view: 'sheepdailys',
    mounts: Object.freeze(['SheepDailyPage']),
    reason: 'Hub at the index, daily record for /sheep/dailys/<id>.',
  }),
  'src/sheep/SheepBatchesView.tsx::SheepBatchesRouter': Object.freeze({
    disposition: 'splits',
    view: 'sheepbatches',
    mounts: Object.freeze(['SheepBatchPage']),
    reason: 'Hub at the index, batch record for /sheep/batches/<id>.',
  }),
  'src/sheep/SheepFlocksView.tsx::SheepFlocksRouter': Object.freeze({
    disposition: 'splits',
    view: 'sheepflocks',
    mounts: Object.freeze(['SheepAnimalPage']),
    reason: 'Hub at the index, sheep record for /sheep/flocks/<id>.',
  }),
  'src/tasks/TaskCenterView.tsx::TasksRouter': Object.freeze({
    disposition: 'splits',
    view: 'tasks',
    mounts: Object.freeze(['TodoItemPage', 'TaskInstancePage']),
    reason:
      'Four URL shapes, three surfaces: /tasks and /tasks/todo are the same component remounted under a distinct key, then the two record pages.',
  }),
  'src/webforms/FuelingHub.tsx::FuelingHub': Object.freeze({
    disposition: 'splits',
    view: 'fuelingHub',
    mounts: Object.freeze(['EquipmentFuelingWebform', 'FuelSupplyWebform']),
    reason: 'subRoute hub | form | supply, computed from the pathname.',
  }),
  'src/equipment/EquipmentHome.tsx::EquipmentHome': Object.freeze({
    disposition: 'splits',
    view: 'equipmentHome',
    mounts: Object.freeze([]),
    reason:
      'Five subViews computed from /fleet/*, rendered as inline conditionals rather than returns. The two entry pages are distinct components; fleet and fuel-log are two views of the same loaded arrays and share one surface.',
  }),
  'src/webforms/WebformHub.tsx::WebformHub': Object.freeze({
    disposition: 'sub-mode',
    view: 'webformhub',
    reason:
      'activeForm comes from /dailys/<form> and selects which form fields render inside the same component, behind the same webform_config read. Six modes, one mount, one load.',
  }),
  'src/processing/ProcessingCalendarView.tsx::ProcessingCalendarView': Object.freeze({
    disposition: 'not-a-surface',
    view: 'processing',
    reason:
      'Reads the pathname only to preserve the Processing URL during navigation. Search parameters select schedule or local-only Labels inside the existing processing view; there is no pathname branch, and only the schedule mounts its data resource.',
  }),
  'src/processing/ProcessingCalendarView.tsx::ProcessingScheduleContent': Object.freeze({
    disposition: 'not-a-surface',
    view: 'processing',
    reason:
      'Owns the existing Processing schedule and drawer resource. Reads the pathname only to rebuild its own URL when the ?record param changes; the drawer is selected by search state, not by a pathname branch.',
  }),
  'src/processing/labels/FreezerLabels.tsx::FreezerLabels': Object.freeze({
    disposition: 'sub-mode',
    view: 'processing',
    reason:
      'The hash can open a label preview within this same local-only editor. Home, editing, alignment and print modes share its mounted local state, with no server read or pathname-selected surface.',
  }),
  'src/equipment/OwnerEquipmentReports.tsx::ReportList': Object.freeze({
    disposition: 'not-a-surface',
    reason:
      'My Equipment Reports (228). Reads the location only for the ?report= search parameter that opens the owner editor drawer over the same list and the same equipment.ownReports resource; there is no pathname branch and no second mount.',
  }),
  'src/projects/ProjectsRoute.tsx::ProjectsRoute': Object.freeze({
    disposition: 'not-a-surface',
    view: 'projects',
    reason:
      'Projects (256). Reads the location only for the ?job= search parameter that opens the job drawer over the same page and the same projects.jobs resource, and to rebuild that URL when the drawer opens or closes; there is no pathname branch and no second mount.',
  }),
  'src/app/PageBoundary.tsx::PageBoundary': Object.freeze({
    disposition: 'not-a-surface',
    reason:
      'The per-page crash boundary around App (Design Constitution N12). It reads the pathname only as a reset key so a crash clears when the route changes; it selects no surface and loads nothing.',
  }),
  'src/shared/Header.tsx::Header': Object.freeze({
    disposition: 'not-a-surface',
    reason:
      'Mounted on essentially every surface. Reads the pathname for active-tab highlighting and for go(), and selects no render of its own.',
  }),
  'src/auth/SetPasswordScreen.tsx::SetPasswordScreen': Object.freeze({
    disposition: 'not-a-surface',
    reason:
      'A pre-gate shell already declared as the auth.shell route-oracle exception. It reads the pathname to detect the recovery/invite link, not to choose between surfaces.',
  }),
  'src/newsletter/NewsletterPublicApp.tsx::NewsletterPublicApp': Object.freeze({
    disposition: 'sub-mode',
    reason:
      'The public.newsletter exception surface. Archive vs issue is chosen inside it from the slug, behind the same three anon RPCs — one anonymous surface either way.',
  }),
  'src/layer/LayerBatchesView.tsx::LayerBatchesHub': Object.freeze({
    disposition: 'not-a-surface',
    reason:
      'A branch target of LayerBatchesRouter, already declared as layer.batches.index. It reads the pathname for navigation only.',
  }),
  'src/sheep/SheepBatchesView.tsx::SheepBatchesHub': Object.freeze({
    disposition: 'not-a-surface',
    reason:
      'A branch target of SheepBatchesRouter, already declared as sheep.batches.index. It reads the pathname for navigation only.',
  }),
  'src/layer/RetirementHomeHistory.tsx::RetirementHomeHistory': Object.freeze({
    disposition: 'not-a-surface',
    reason:
      'A disclosure inside the Retirement Home screen, not a route branch. It reads location.hash — never the pathname — to auto-open itself when a #comment-<id> deep link points at one of its collaboration threads. What that chooses is whether the <details> is expanded; the content inside is mounted conditionally on that open state, and a user can toggle it by hand. No surface swap depends on it.',
  }),
});

// Surfaces the route tables cannot enumerate, because they are not keyed on
// `view`. The exception applies ONLY to the oracle's route-row join: an
// authenticated surface listed here still carries a full manifest and still
// participates in resource/call equality. Being unroutable is not being exempt
// from ownership.
export const ROUTE_ORACLE_EXCEPTIONS: readonly RouteOracleException[] = Object.freeze([
  Object.freeze({
    surface: 'public.newsletter',
    authenticated: false,
    matchContract: 'renders before the LoginScreen gate; the only view outside the global data gate',
  }),
  Object.freeze({
    surface: 'broiler.batchForm.fullscreen',
    authenticated: true,
    matchContract:
      "showForm && inBroilerSpace && !pathname.startsWith('/broiler/batches/') — keyed on form state AND the broiler host, not on view. The host half is load-bearing: as a bare blocklist the form pinned itself over 21 non-broiler destinations after a browser Back.",
  }),
  Object.freeze({
    surface: 'auth.shell',
    authenticated: false,
    matchContract: 'LoginScreen / password recovery / set-password shells, rendered before any view dispatch',
  }),
]);

const LEGACY_ALL: readonly Obligation[] = Object.freeze([Object.freeze({id: 'legacy.all', owner: 'legacy'})]);

// This is deliberately an explicit owner, not a catalog resource with empty
// tables/rpcs/buckets. A resource without a physical read would make the catalog proof
// vacuous; this sentinel means the opposite: the receipt and source proof must
// establish that the surface has no route-local server read to own. A receipt
// must also name any independently-owned shared shell or external chrome that
// can be mounted beside that route surface.
const NO_SERVER_READ: readonly Obligation[] = Object.freeze([
  Object.freeze({id: 'no-server-read', owner: 'no-server-read'}),
]);

const NO_RESIDUAL_LEGACY: readonly ResidualLegacyId[] = Object.freeze([]);

// ---------------------------------------------------------------------------
// MIGRATED SURFACES — discovered, never listed
// ---------------------------------------------------------------------------
// These surfaces load through the route data catalog, so their route-local
// obligation is owned by a `permanent` resource rather than by `legacy`. The
// one-owner rule makes a half-migrated resource state unrepresentable: a
// declaration REPLACES legacy.all for its route-local resource in the same
// change, and the physical reads it displaced move to `retired` in
// render-read-sites.json in the same change.
//
// A route resource migration alone is not a released-render claim. A
// declaration must therefore also say which App-context values it still
// consumes in `residualLegacy`; a non-empty list keeps the surface explicitly
// `partial` until those values have local owners.
//
// WHY THIS IS A GLOB AND NOT A MAP
//
// It used to be a central `MIGRATED` object right here. That made this file a
// merge collision point for every localization at once, and the cost was
// measured rather than predicted: two independent explorations
// (`equipment.home` and `layer.batches`) each edited the SAME four files —
// this one, routeDataCatalog.ts, its test, and the manifest guard — and could
// not be combined. With 69 surfaces still to migrate, that serializes the whole
// programme behind one object.
//
// Discovery is now an eager glob over `./migratedSurfaces/`, one frozen
// declaration per surface. Two surfaces migrating concurrently touch two
// different files and never collide here.
//
// Deleting the central list would have WEAKENED the proof, though: if a
// declaration module vanished, this file would use the legacy.all sentinel and
// a one-way resource join would still pass. So the ledger's independent,
// monotonic role moved to `tests/fixtures/migrated-surface-receipts/`, and the
// guard requires exact three-way equality between the manifest, the catalog and
// those receipts. Coordinated deletion or remapping in BOTH the declaration and
// the catalog is still red against the receipt.
// ---------------------------------------------------------------------------

/** The entire content of one `migratedSurfaces/*.ts` module. */
export type MigratedSurfaceDeclaration = {
  readonly surface: string;
  readonly obligations: readonly Obligation[];
  readonly residualLegacy: readonly ResidualLegacyId[];
};

const OWNER_VOCABULARY: ReadonlySet<string> = new Set<string>(['legacy', 'permanent', 'temporary-local']);
const RESIDUAL_LEGACY_VOCABULARY: ReadonlySet<string> = new Set<string>(RESIDUAL_LEGACY_IDS);

/**
 * Fold discovered declaration modules into one frozen surface -> declaration
 * map, rejecting every shape that would let ownership become ambiguous.
 *
 * Pure and exported so the guard can drive it directly with malformed inputs.
 * Proving these rejections only through the real glob would mean the guard
 * could never see most of them fail.
 */
export function aggregateMigratedSurfaces(
  modules: Readonly<Record<string, unknown>>,
  allowedSurfaces: ReadonlySet<string>,
): Readonly<Record<string, MigratedSurfaceDeclaration>> {
  const out: Record<string, MigratedSurfaceDeclaration> = {};

  for (const modulePath of Object.keys(modules)) {
    const loaded = modules[modulePath];
    const declared = loaded && typeof loaded === 'object' ? (loaded as {default?: unknown}).default : undefined;
    if (!declared || typeof declared !== 'object' || Array.isArray(declared)) {
      throw new Error(`${modulePath}: must default-export exactly one migrated-surface declaration object`);
    }

    const keys = Object.keys(declared).sort();
    if (keys.length !== 3 || keys[0] !== 'obligations' || keys[1] !== 'residualLegacy' || keys[2] !== 'surface') {
      throw new Error(
        `${modulePath}: migrated-surface declaration must contain exactly {surface, obligations, residualLegacy}`,
      );
    }

    const surface = (declared as {surface?: unknown}).surface;
    if (typeof surface !== 'string' || surface.trim() === '') {
      throw new Error(`${modulePath}: declares a blank surface id`);
    }

    const rawObligations = (declared as {obligations?: unknown}).obligations;
    if (!Array.isArray(rawObligations) || rawObligations.length === 0) {
      throw new Error(`${modulePath}: surface ${surface} must declare a non-empty obligations array`);
    }

    const rawResidualLegacy = (declared as {residualLegacy?: unknown}).residualLegacy;
    if (!Array.isArray(rawResidualLegacy)) {
      throw new Error(`${modulePath}: surface ${surface} must declare a residualLegacy array`);
    }

    const normalized: Obligation[] = [];
    const seenObligationId = new Set<string>();
    const owners = new Set<string>();
    for (const raw of rawObligations as readonly unknown[]) {
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        throw new Error(`${modulePath}: surface ${surface} has a malformed obligation`);
      }
      const id = (raw as {id?: unknown}).id;
      const owner = (raw as {owner?: unknown}).owner;
      if (typeof id !== 'string' || id.trim() === '') {
        throw new Error(`${modulePath}: surface ${surface} has a blank obligation id`);
      }
      if (typeof owner !== 'string' || !OWNER_VOCABULARY.has(owner)) {
        throw new Error(`${modulePath}: surface ${surface} obligation ${id} has unknown owner ${String(owner)}`);
      }
      if (seenObligationId.has(id)) {
        throw new Error(`${modulePath}: surface ${surface} declares obligation ${id} twice`);
      }
      seenObligationId.add(id);
      owners.add(owner);
      normalized.push(Object.freeze({id, owner: owner as ObligationOwner}));
    }

    if (owners.has('legacy') && owners.has('permanent')) {
      throw new Error(
        `${modulePath}: surface ${surface} claims both legacy and permanent ownership. ` +
          'A migration removes legacy.all in the same change; holding both is the exact ambiguity the one-owner rule exists to prevent.',
      );
    }

    const residualLegacy: ResidualLegacyId[] = [];
    const seenResidualLegacy = new Set<string>();
    for (const raw of rawResidualLegacy as readonly unknown[]) {
      if (typeof raw !== 'string' || raw.trim() === '') {
        throw new Error(`${modulePath}: surface ${surface} has a blank residual legacy id`);
      }
      if (!RESIDUAL_LEGACY_VOCABULARY.has(raw)) {
        throw new Error(`${modulePath}: surface ${surface} has an unknown residual legacy id ${raw}`);
      }
      if (seenResidualLegacy.has(raw)) {
        throw new Error(`${modulePath}: surface ${surface} declares residual legacy id ${raw} twice`);
      }
      seenResidualLegacy.add(raw);
      residualLegacy.push(raw as ResidualLegacyId);
    }

    if (!allowedSurfaces.has(surface)) {
      throw new Error(
        `${modulePath}: ${surface} is not an authenticated surface of the current topology, ` +
          'so nothing would ever consume this declaration.',
      );
    }

    // Own-property check BEFORE inserting. Object spread and Object.fromEntries
    // let the last module silently win, which would hide a duplicated surface.
    if (Object.prototype.hasOwnProperty.call(out, surface)) {
      throw new Error(`${modulePath}: ${surface} is already declared by another module`);
    }

    out[surface] = Object.freeze({
      surface,
      obligations: Object.freeze(normalized),
      residualLegacy: Object.freeze(residualLegacy),
    });
  }

  return Object.freeze(out);
}

// ---------------------------------------------------------------------------
// NO-SERVER-READ SURFACES - an absence claim with its own declaration class
// ---------------------------------------------------------------------------
// A no-server-read surface is neither a permanent catalog resource nor a
// legacy read. It has no route-local data-read obligation at all; its receipt
// records any shared shell or external chrome that remains independently
// owned. Keeping these modules in a separate glob means a future resource
// migration cannot accidentally turn an empty catalog entry into proof of
// absence, and it keeps normal per-resource declarations collision-free.

export type NoServerReadSurfaceDeclaration = {
  readonly surface: string;
  readonly disposition: 'no-server-read';
};

/**
 * Fold no-server-read declarations into a frozen surface map. The exact shape
 * deliberately leaves no extension point for a hidden resource ID or a
 * fail-open flag: the companion receipt/source proof is what establishes the
 * absence of a route-local server read (plus the explicit boundary for any
 * independently-owned shared shell/external chrome).
 */
export function aggregateNoServerReadSurfaces(
  modules: Readonly<Record<string, unknown>>,
  allowedSurfaces: ReadonlySet<string>,
): Readonly<Record<string, NoServerReadSurfaceDeclaration>> {
  const out: Record<string, NoServerReadSurfaceDeclaration> = {};

  for (const modulePath of Object.keys(modules)) {
    const loaded = modules[modulePath];
    const declared = loaded && typeof loaded === 'object' ? (loaded as {default?: unknown}).default : undefined;
    if (!declared || typeof declared !== 'object' || Array.isArray(declared)) {
      throw new Error(`${modulePath}: must default-export exactly one no-server-read declaration object`);
    }

    const keys = Object.keys(declared).sort();
    if (keys.length !== 2 || keys[0] !== 'disposition' || keys[1] !== 'surface') {
      throw new Error(`${modulePath}: no-server-read declaration must contain exactly {surface, disposition}`);
    }

    const surface = (declared as {surface?: unknown}).surface;
    if (typeof surface !== 'string' || surface.trim() === '') {
      throw new Error(`${modulePath}: declares a blank no-server-read surface id`);
    }
    if ((declared as {disposition?: unknown}).disposition !== 'no-server-read') {
      throw new Error(`${modulePath}: ${surface} must use the literal no-server-read disposition`);
    }
    if (!allowedSurfaces.has(surface)) {
      throw new Error(
        `${modulePath}: ${surface} is not an authenticated surface of the current topology, ` +
          'so nothing would ever consume this no-server-read declaration.',
      );
    }
    if (Object.prototype.hasOwnProperty.call(out, surface)) {
      throw new Error(`${modulePath}: ${surface} is already declared by another no-server-read module`);
    }

    out[surface] = Object.freeze({surface, disposition: 'no-server-read'});
  }

  return Object.freeze(out);
}

function assertDisjointSurfaceDeclarations(
  migrated: Readonly<Record<string, MigratedSurfaceDeclaration>>,
  noServerRead: Readonly<Record<string, NoServerReadSurfaceDeclaration>>,
): void {
  for (const surface of Object.keys(noServerRead)) {
    if (Object.prototype.hasOwnProperty.call(migrated, surface)) {
      throw new Error(
        `${surface} is declared both permanent and no-server-read; a surface must choose exactly one non-legacy disposition.`,
      );
    }
  }
}

// The surfaces that may legitimately be declared, derived from the authenticated
// topology ABOVE any migration: every ordinary authenticated view surface, every
// variant in place of its parent, and every authenticated oracle exception.
function deriveAllowedAuthenticatedSurfaces(): ReadonlySet<string> {
  const allowed = new Set<string>();
  for (const view of AUTHENTICATED_VIEWS) {
    const variants = VIEW_VARIANTS[view];
    if (variants) {
      for (const variant of variants) allowed.add(variant.surface);
      continue;
    }
    allowed.add(`view.${view}`);
  }
  for (const exception of ROUTE_ORACLE_EXCEPTIONS) {
    if (exception.authenticated) allowed.add(exception.surface);
  }
  return allowed;
}

const allowedAuthenticatedSurfaces: ReadonlySet<string> = deriveAllowedAuthenticatedSurfaces();

const migratedSurfaceModules = import.meta.glob('./migratedSurfaces/*.ts', {
  eager: true,
});

const noServerReadSurfaceModules = import.meta.glob('./noServerReadSurfaces/*.ts', {
  eager: true,
});

const migratedSurfaces = aggregateMigratedSurfaces(migratedSurfaceModules, allowedAuthenticatedSurfaces);
const noServerReadSurfaces = aggregateNoServerReadSurfaces(noServerReadSurfaceModules, allowedAuthenticatedSurfaces);

assertDisjointSurfaceDeclarations(migratedSurfaces, noServerReadSurfaces);

function buildManifest(): Readonly<Record<string, SurfaceEntry>> {
  // Which declarations actually reached a rendered surface. A typo'd or orphan
  // module that no surface consumes would otherwise sit unused beside a manifest
  // that had silently used the legacy.all validation sentinel.
  const consumedMigrated = new Set<string>();
  const consumedNoServerRead = new Set<string>();

  const surfaceEntry = (surface: string, view: string | null, matchContract: string): SurfaceEntry => {
    // Own-property lookup, never truthiness: a surface literally named
    // "constructor" or "toString" would otherwise inherit a value from
    // Object.prototype and be treated as migrated.
    const migrated = Object.prototype.hasOwnProperty.call(migratedSurfaces, surface) ? migratedSurfaces[surface] : null;
    const noServerRead = Object.prototype.hasOwnProperty.call(noServerReadSurfaces, surface)
      ? noServerReadSurfaces[surface]
      : null;
    if (migrated) consumedMigrated.add(surface);
    if (noServerRead) consumedNoServerRead.add(surface);
    const residualLegacy = migrated ? migrated.residualLegacy : NO_RESIDUAL_LEGACY;
    const migrationStatus = noServerRead
      ? SURFACE_MIGRATION_STATUS.NO_SERVER_READ
      : migrated
        ? residualLegacy.length > 0
          ? SURFACE_MIGRATION_STATUS.PARTIAL
          : SURFACE_MIGRATION_STATUS.PERMANENT
        : SURFACE_MIGRATION_STATUS.LEGACY;
    return Object.freeze({
      surface,
      view,
      matchContract,
      migrationStatus,
      // Validation fallback for a missing declaration: one legacy sentinel.
      // Migrating a surface replaces this entry with permanent resources in the
      // SAME change — there is never a committed state with both owners or
      // neither.
      obligations: noServerRead ? NO_SERVER_READ : migrated ? migrated.obligations : LEGACY_ALL,
      residualLegacy,
    });
  };

  const out: Record<string, SurfaceEntry> = {};
  for (const view of AUTHENTICATED_VIEWS) {
    const variants = VIEW_VARIANTS[view];
    if (variants) {
      for (const v of variants) out[v.surface] = surfaceEntry(v.surface, view, v.matchContract);
      continue;
    }
    out[`view.${view}`] = surfaceEntry(`view.${view}`, view, `route row for view '${view}'`);
  }
  for (const ex of ROUTE_ORACLE_EXCEPTIONS) {
    if (!ex.authenticated) continue;
    out[ex.surface] = surfaceEntry(ex.surface, null, ex.matchContract);
  }

  const assertAllConsumed = (
    label: string,
    discoveredMap: Readonly<Record<string, unknown>>,
    consumed: ReadonlySet<string>,
  ) => {
    const discovered = Object.keys(discoveredMap).sort();
    const used = [...consumed].sort();
    if (discovered.length === used.length && discovered.every((key, index) => key === used[index])) return;
    throw new Error(
      `${label} declarations were discovered but never consumed by the manifest: ` +
        `discovered [${discovered.join(', ')}], consumed [${used.join(', ')}].`,
    );
  };

  assertAllConsumed('migrated-surface', migratedSurfaces, consumedMigrated);
  assertAllConsumed('no-server-read', noServerReadSurfaces, consumedNoServerRead);

  return Object.freeze(out);
}

export const SURFACE_MANIFEST: Readonly<Record<string, SurfaceEntry>> = buildManifest();

/**
 * Production browser-entry consumer for the eager declaration globs.
 *
 * This must stay synchronous and data-free. It makes the ownership declarations
 * runtime-reachable without choosing a route, creating a query, or changing
 * authenticated route disposition; App owns those later decisions.
 */
export function assertSurfaceDataManifestRuntime(): void {
  const entries = Object.entries(SURFACE_MANIFEST);
  if (entries.length === 0) throw new Error('surface data manifest is empty at browser startup');

  for (const [surface, entry] of entries) {
    if (entry.surface !== surface || entry.obligations.length === 0 || !Array.isArray(entry.residualLegacy)) {
      throw new Error(`surface data manifest has an invalid entry for ${surface}`);
    }
    const noServerRead = entry.obligations.filter((obligation) => obligation.owner === 'no-server-read');
    if (
      noServerRead.length > 0 &&
      (noServerRead.length !== 1 || entry.obligations.length !== 1 || noServerRead[0].id !== 'no-server-read')
    ) {
      throw new Error(`surface data manifest has an invalid no-server-read disposition for ${surface}`);
    }

    const permanentOnly = entry.obligations.every((obligation) => obligation.owner === 'permanent');
    const residualIds = new Set<string>();
    for (const residual of entry.residualLegacy) {
      if (!RESIDUAL_LEGACY_VOCABULARY.has(residual) || residualIds.has(residual)) {
        throw new Error(`surface data manifest has an invalid residual legacy disposition for ${surface}`);
      }
      residualIds.add(residual);
    }

    if (
      entry.migrationStatus === SURFACE_MIGRATION_STATUS.PARTIAL &&
      (!permanentOnly || entry.residualLegacy.length === 0)
    ) {
      throw new Error(`surface data manifest has an invalid partial migration disposition for ${surface}`);
    }
    if (
      entry.migrationStatus === SURFACE_MIGRATION_STATUS.PERMANENT &&
      (!permanentOnly || entry.residualLegacy.length !== 0)
    ) {
      throw new Error(`surface data manifest has an invalid permanent migration disposition for ${surface}`);
    }
    if (
      entry.migrationStatus === SURFACE_MIGRATION_STATUS.LEGACY &&
      (entry.obligations.length !== 1 ||
        entry.obligations[0].owner !== 'legacy' ||
        entry.obligations[0].id !== 'legacy.all' ||
        entry.residualLegacy.length !== 0)
    ) {
      throw new Error(`surface data manifest has an invalid legacy migration disposition for ${surface}`);
    }
    if (
      entry.migrationStatus === SURFACE_MIGRATION_STATUS.NO_SERVER_READ &&
      (noServerRead.length !== 1 || entry.residualLegacy.length !== 0)
    ) {
      throw new Error(`surface data manifest has an invalid no-server-read migration disposition for ${surface}`);
    }
    if (!Object.values(SURFACE_MIGRATION_STATUS).includes(entry.migrationStatus)) {
      throw new Error(`surface data manifest has an unknown migration disposition for ${surface}`);
    }
  }
}

export const OBLIGATION_OWNERS: readonly ObligationOwner[] = Object.freeze([
  'legacy',
  'permanent',
  'temporary-local',
  'no-server-read',
]);
